import React, { useEffect, useState } from "react";

import { useParams } from "react-router";
import { useNavigate } from "react-router";
import { ConfigProvider, Modal, Spin } from "antd";

import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";
import { Button } from "@companion-core/shared/app/Components/Button";
import { useTransparentHeader } from "@companion-core/shared/app/Hooks/transparentHeader";
import { Banner } from "@companion-core/shared/app/Components/Banner";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { Carousel } from "@companion-core/shared/app/Components/Carousel";
import { useWebNavigationHeader } from "@companion-core/web/src/Hooks/navigationHeader";
import { RequestDetailContent } from "../RequestDetail/requestDetail.model";
import { getCloudinaryOptimizedUrl } from "@companion-core/shared/app/Utils/cloudinary";
import { getSanitizedDescriptionAlt } from "@companion-core/shared/app/Utils/componentHelper";
import { useConfigStore } from "@companion-core/shared/app/Store/configStore";
import { RequestRecommendationHeaderProps } from "@companion-core/shared/app/Components/Requests/RequestRecommendationDetail/requestRecommendationDetail.model";
import { useRequestRecommendationDetail } from "@companion-core/shared/app/Components/Requests/RequestRecommendationDetail/hooks/useRequestRecommendationDetail";
import { PopIn } from "@companion-core/shared/app/Components/PopIn";
import { redirectTo } from "@companion-core/web/src/Utilities/navigation";
import { Map } from "@companion-core/shared/app/Components/Map";
import { ImageSlider } from "@companion-core/shared/app/Components/ImageSlider";
import {
  trackClickRequestChooseRecommendation,
  trackClickRequestMapsRecommendation,
} from "@companion-core/shared/app/tracking/requests";

import "@companion-core/web/src/Assets/Styles/scss/myRequestsRecommendationDetail.scss";

const HEIGHT_BREAKPOINT = 250;

const RequestRecommendationHeader = ({
  images,
  isMobileSize,
  t,
  setImageSliderIsOpen,
}: RequestRecommendationHeaderProps) => {
  const { config } = useConfigStore();

  if (images && images.length > 0) {
    if (isMobileSize && images.length > 1) {
      return (
        <Carousel autoplay={false} testID="recommendation__carousel">
          {images
            .sort((a, b) => a.order - b.order)
            .map((image) => (
              <div key={image.order}>
                <img
                  className="recommendation__slide-image"
                  onClick={() => setImageSliderIsOpen?.(true)}
                  src={getCloudinaryOptimizedUrl(image.url, {
                    contentType: "banner",
                    width: window.innerWidth,
                  })}
                  alt={getSanitizedDescriptionAlt(image.description_alt) ?? ""}
                />
              </div>
            ))}
        </Carousel>
      );
    } else {
      return (
        <div onClick={() => (isMobileSize ? setImageSliderIsOpen?.(true) : null)}>
          <Banner
            imageUrl={getCloudinaryOptimizedUrl(images[0].url, {
              contentType: "banner",
              width: window.innerWidth,
            })}
            shadowType="gradient"
            boxShadow>
            {!isMobileSize ? (
              <div className="recommendation__banner-cta-wrapper">
                <Button
                  label={t?.("request.my-requests.recommendation.CTA.show-more") ?? ""}
                  testID="recommendation__banner-cta"
                  type="tertiary"
                  onClick={() => setImageSliderIsOpen?.(true)}
                  classNames={["recommendation__banner-cta"]}
                />
              </div>
            ) : undefined}
          </Banner>
        </div>
      );
    }
  } else {
    return (
      <Banner
        imageUrl={getCloudinaryOptimizedUrl(
          config?.modules?.concierge?.modules["my-requests"]?.assets?.recommendationPlaceholder,
          {
            contentType: "banner",
            width: window.innerWidth,
          }
        )}
        shadowType="gradient"
        boxShadow
      />
    );
  }
};

export const RequestRecommendationDetail = () => {
  const navigate = useNavigate();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const { id, proposalId, recommendationId } = useParams();
  const {
    status,
    detail,
    images,
    price,
    title,
    beginningAddress,
    endingAddress,
    isPending,
    isError,
    t,
    isAccepting,
    sendRequest,
    isPopInOpen,
    popInArgs,
    vendorId,
  } = useRequestRecommendationDetail({
    requestId: id,
    proposalId,
    recommendationId,
    onAcceptProposalSuccess: () => navigate(`/my-requests/cross-sell/${id}`),
  });
  const [scrollPosition, setScrollPosition] = useState(0);
  const [imageSliderIsOpen, setImageSliderIsOpen] = useState(false);
  const { navigateBackward } = useWebNavigationHeader();
  const { publicConfig } = useConfigStore();
  useTransparentHeader();

  const handleScroll = () => {
    const position = window.scrollY;
    setScrollPosition(position);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isError) {
      navigate(`/my-requests/${id}/details/proposals`);
    }
  }, [isError]);

  return isPending ? (
    <Spin className="recommendation__loader" />
  ) : (
    <div className="recommendation" data-testid="recommendation">
      {popInArgs && <PopIn isOpen={isPopInOpen} {...popInArgs} />}
      <ConfigProvider
        theme={{
          components: {
            Modal: {
              contentBg: "#000",
            },
          },
        }}>
        <Modal
          open={imageSliderIsOpen}
          width={"100%"}
          height={"100%"}
          className="recommendation__image-slider-modal"
          zIndex={2000}
          footer={<></>}
          closeIcon={
            <div
              className="recommendation__image-slider-modal__close-background"
              onClick={() => setImageSliderIsOpen(false)}
              data-testid="image-slider-modal__close">
              <FontIcon
                name="JP-Close-bold"
                className="recommendation__image-slider-modal__close"
                size={24}
                data-testid="newsletter__close-icon"
              />
            </div>
          }>
          <ImageSlider images={images} />
        </Modal>
      </ConfigProvider>
      <RequestRecommendationHeader
        images={images}
        isMobileSize={isMobileSize}
        t={t}
        setImageSliderIsOpen={setImageSliderIsOpen}
      />
      <div className="recommendation__layout">
        <div className="recommendation__content">
          <h1
            data-testid="recommendation__title"
            className="recommendation__title title-7"
            onClick={
              !isMobileSize
                ? () =>
                    navigateBackward(`/navigate/${id}/details/${RequestDetailContent.proposals}`)
                : undefined
            }>
            {!isMobileSize && <FontIcon name="JP-Arrow-left-bold" size={18} />}
            {title}
          </h1>
          <div className="recommendation__content-wrapper">
            {price && (
              <p
                data-testid="recommendation__price"
                className="recommendation__price body-text-5"
                // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(price),
                }}
              />
            )}
            {beginningAddress && (
              <a
                href="#map"
                className="recommendation__addresses"
                data-testid="recommendation__addresses">
                <FontIcon name="JP-Location" size={24} />
                <span className="body-text-6">{beginningAddress.formatted_address}</span>
                {endingAddress && (
                  <>
                    <FontIcon name="JP-Right" size={24} />
                    <span className="body-text-6">{endingAddress.formatted_address}</span>
                  </>
                )}
              </a>
            )}
            {detail && (
              <p
                data-testid="recommendation__detail"
                className="recommendation__detail body-text-6"
                // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(detail),
                }}
              />
            )}
            {beginningAddress && (
              <div id="map" className="recommendation__map">
                <Map
                  beginningAddress={beginningAddress}
                  endingAddress={endingAddress}
                  APIKey={publicConfig?.data?.google_maps_web_api_key}
                  className="recommendation__map-wrapper"
                />
                <div>
                  <p className="recommendation__addresses">
                    <span className="body-text-6">{beginningAddress.formatted_address}</span>
                    {endingAddress && (
                      <>
                        <FontIcon name="JP-Right" size={24} />
                        <span className="body-text-6">{endingAddress.formatted_address}</span>
                      </>
                    )}
                  </p>
                  <Button
                    label={t("request.my-requests.recommendation.map-CTA.default")}
                    testID="recommendation__map-button"
                    type="secondary"
                    onClick={() => {
                      trackClickRequestMapsRecommendation({
                        status,
                        requestId: id,
                        recommendationId,
                        vendorId,
                      });
                      redirectTo(
                        `https://www.google.com/maps/dir/Current+Location/'${beginningAddress.latitude},${beginningAddress.longitude}'`,
                        "external",
                        undefined,
                        "blank"
                      );
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {status === "recommended" && (
          <div className="recommendation__booking">
            <div
              className={`recommendation__booking-content ${scrollPosition > HEIGHT_BREAKPOINT && !isMobileSize ? "recommendation__booking--fixed" : ""}`}>
              <p className="recommendation__booking-title body-text-6">{title}</p>
              <div className="recommendation__booking-CTA">
                <Button
                  size={isMobileSize ? "full" : "large"}
                  loading={isAccepting}
                  label={t("request.my-requests.recommendation.CTA.default")}
                  testID="recommendation__booking-CTA"
                  onClick={() => {
                    trackClickRequestChooseRecommendation({
                      status,
                      requestId: id,
                      recommendationId,
                      vendorId,
                    });
                    sendRequest("acceptation");
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
